import { Injectable } from '@angular/core';
import {
  FlowStepType,
  FlowType,
  IActivity,
  IAmenity,
  IBoondock,
  IDtoListingBooking,
  IDtoListingLocation,
  IDtoSignUpPriceStep,
  IDtoSiteAmenities,
  IDtoSiteDetails,
  IDtoSiteIsolation,
  IDtoSiteName,
  ILandType,
  IListing,
  IPhoto,
  IPolicy,
  IRV,
  ISignalReception,
  ISignUpFlowStep,
  ISite,
  IUser,
  IVehicle,
} from '@curbnturf/entities';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as SignUpActions from './sign-up-state.actions';
import { SignUpStateState } from './sign-up-state.reducer';
import * as SignUpStateSelectors from './sign-up-state.selectors';

@Injectable({
  providedIn: 'root',
})
export class SignUpStateFacade {
  signUpState$: Observable<SignUpStateState>;
  spinner$: Observable<boolean>;
  steps$: Observable<{
    steps: ISignUpFlowStep[];
    currentStep: ISignUpFlowStep | undefined;
    previousStep: ISignUpFlowStep | undefined;
    nextStep: ISignUpFlowStep | undefined;
    isLastStep: boolean;
    isFirstStep: boolean;
  }>;
  user$: Observable<IUser | undefined>;
  listing$: Observable<IListing | undefined>;
  sites$: Observable<ISite[]>;
  selectedSite$: Observable<ISite | undefined>;
  rv$: Observable<IRV | undefined>;
  percentComplete$: Observable<number>;
  boondock$: Observable<IBoondock | undefined>;
  isLastStep$: Observable<boolean>;
  isFirstStep$: Observable<boolean>;
  guest$: Observable<boolean>;
  flowType$: Observable<FlowType>;

  constructor(private store: Store<SignUpStateState>) {
    this.signUpState$ = this.store.pipe(select(SignUpStateSelectors.getSignUpStateState));
    this.spinner$ = this.store.pipe(select(SignUpStateSelectors.getSpinner));
    this.steps$ = this.store.pipe(select(SignUpStateSelectors.getCompleteSteps));
    this.user$ = this.store.pipe(select(SignUpStateSelectors.getUser));
    this.listing$ = this.store.pipe(select(SignUpStateSelectors.getListing));
    this.sites$ = this.store.pipe(select(SignUpStateSelectors.getListingSites));
    this.selectedSite$ = this.store.pipe(select(SignUpStateSelectors.getSelectedSite));
    this.rv$ = this.store.pipe(select(SignUpStateSelectors.getRv));
    this.percentComplete$ = this.store.pipe(select(SignUpStateSelectors.getPercentComplete));
    this.boondock$ = this.store.pipe(select(SignUpStateSelectors.getBoondock));
    this.isLastStep$ = this.store.pipe(select(SignUpStateSelectors.isLastStep));
    this.isFirstStep$ = this.store.pipe(select(SignUpStateSelectors.isFirstStep));
    this.guest$ = this.store.pipe(select(SignUpStateSelectors.getGuest));
    this.flowType$ = this.store.pipe(select(SignUpStateSelectors.getFlowType));
  }

  init() {
    this.store.dispatch(SignUpActions.init());
  }

  setState(signUp: SignUpStateState) {
    this.store.dispatch(SignUpActions.setState(signUp));
  }

  redirect(url: string) {
    this.store.dispatch(SignUpActions.redirect({ routeTo: url }));
  }

  next(override?: string) {
    this.store.dispatch(SignUpActions.nextStep({ override }));
  }

  previous(override?: string) {
    this.store.dispatch(SignUpActions.previousStep({ override }));
  }

  setCurrentStep(step: FlowStepType) {
    this.store.dispatch(SignUpActions.setCurrentStepType({ step }));
  }

  startGuestFlow(user: IUser) {
    this.store.dispatch(SignUpActions.startGuestFlow({ user }));
  }

  startHostFlow(user: IUser) {
    this.store.dispatch(SignUpActions.startHostFlow({ user }));
  }

  saveSourceStep(user: IUser) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveSourceStep({ user }));
  }

  savePhoneStep(user: IUser, assistance: boolean | null | undefined) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.savePhoneStep({ user, assistance: assistance ?? true }));
  }

  savePropertyTypeStep(types: ILandType['type'][]) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.savePropertyTypeStep({ types }));
  }

  setBoondockPropertyTypeStep(landTypes: ILandType['type'][]) {
    this.store.dispatch(SignUpActions.setBoondockPropertyTypeStep({ landTypes }));
  }

  saveAmenitiesStep(data: IDtoSiteAmenities) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveAmenitiesStep(data));
  }

  saveBoondockAmenitiesStep(data: { amenities: IAmenity[]; signalReception: ISignalReception[] }) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveBoondockAmenitiesStep(data));
  }

  saveActivitiesStep(activities: IActivity[]) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveActivitiesStep({ activities }));
  }

  saveBoondockActivitiesStep(activities: IActivity[]) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveBoondockActivitiesStep({ activities }));
  }

  saveIsolationStep(data: IDtoSiteIsolation) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveIsolationStep(data));
  }

  saveBoondockLandStep(boondock: IBoondock) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveBoondockLandStep({ boondock }));
  }

  saveBoondockIsolationStep(data: IDtoSiteIsolation) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveBoondockIsolationStep(data));
  }

  saveVehiclesStep(vehicles: IVehicle) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveVehiclesStep(vehicles));
  }

  saveDetailsStep(details: IDtoSiteDetails) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveDetailsStep(details));
  }

  savePriceStep(data: IDtoSignUpPriceStep) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.savePriceStep(data));
  }

  saveBookingStep(dto: IDtoListingBooking) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveBookingStep(dto));
  }

  saveMultiSiteStep(type: 'single' | 'multi') {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveMultiSiteStep({ multisite: type }));
  }

  savePoliciesStep(policies: IPolicy[]) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.savePoliciesStep({ policies }));
  }

  saveLocationStep(listingDto: IDtoListingLocation) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveLocationStep(listingDto));
  }

  saveBoondockLocationStep(listingDto: IDtoListingLocation) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveBoondockLocationStep(listingDto));
  }

  startUserTentFlow() {
    this.store.dispatch(SignUpActions.startUserTentFlow());
  }

  startUserRvFlow() {
    this.store.dispatch(SignUpActions.startUserRvFlow());
  }

  saveUserRvStep(rv: IRV) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveUserRvStep({ rv }));
  }

  saveUserRvSizeStep(rv: IRV) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveUserRvSizeStep({ rv }));
  }

  saveUserTentStep(rv: IRV) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveUserTentStep({ rv }));
  }

  saveUserRoadConditionStep(rv: IRV) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveUserRoadConditionStep({ rv }));
  }

  saveDescriptionStep(data: IDtoSiteName) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveDescriptionStep(data));
  }

  saveBoondockDescriptionStep(data: IDtoSiteName) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveBoondockDescriptionStep(data));
  }

  saveSiteNameStep(data: IDtoSiteName) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveSiteNameStep(data));
  }

  updateProfilePhoto(photo: IPhoto) {
    // Since all the processing here happens on Photo upload there is
    // nothing to save and thus no need to show a spinner here.
    this.store.dispatch(SignUpActions.updateProfilePhoto({ photo }));
  }

  updateListingPhotos(photos: IPhoto[], listingId?: number) {
    this.store.dispatch(SignUpActions.updateListingPhotos({ photos, listingId }));
  }

  saveSitePhotosStep(photos: IPhoto[]) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveSitePhotosStep({ photos }));
  }

  updateBoondockPhotos(photos: IPhoto[], boondockId?: number) {
    this.store.dispatch(SignUpActions.updateBoondockPhotos({ photos, boondockId }));
  }

  savePhotosStep(photos: IPhoto[]) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.savePhotosStep({ photos }));
  }

  saveBoondockPhotosStep(photos: IPhoto[]) {
    this.store.dispatch(SignUpActions.displaySpinner());
    this.store.dispatch(SignUpActions.saveBoondockPhotosStep({ photos }));
  }

  publish() {
    this.store.dispatch(SignUpActions.publishListing());
  }

  boondockPublish() {
    this.store.dispatch(SignUpActions.publishBoondock());
  }

  publishLater() {
    this.store.dispatch(SignUpActions.publishLater());
  }

  boondockPublishLater() {
    this.store.dispatch(SignUpActions.boondockPublishLater());
  }

  salesComplete(listingId: number) {
    this.store.dispatch(SignUpActions.listingSalesComplete({ listingId }));
  }

  displaySpinner() {
    this.store.dispatch(SignUpActions.displaySpinner());
  }

  hideSpinner() {
    this.store.dispatch(SignUpActions.hideSpinner());
  }

  resetProcess() {
    this.store.dispatch(SignUpActions.resetProcess());
    this.store.dispatch(SignUpActions.clearInProgressListing());
  }

  setInProgressListing(listing: IListing) {
    this.store.dispatch(SignUpActions.setInProgressListing({ listing }));
  }

  startNewListingAndFlow(user?: IUser) {
    this.store.dispatch(SignUpActions.startNewListingAndFlow({ user }));
  }

  startNewListing(user?: IUser) {
    this.store.dispatch(SignUpActions.startNewListing({ user }));
  }

  startNewBoondock(user?: IUser) {
    this.store.dispatch(SignUpActions.startNewBoondock({ user }));
  }

  saveClientProfileStep(user: IUser, newsletter?: boolean | null) {
    this.store.dispatch(SignUpActions.saveClientProfileStep({ user, newsletter }));
  }

  editSelectedSite(site: ISite) {
    this.store.dispatch(SignUpActions.editSelectedSite({ selectedSite: site }));
  }

  enableSite(site: ISite) {
    this.store.dispatch(SignUpActions.enableSite({ site }));
  }

  disableSite(site: ISite) {
    this.store.dispatch(SignUpActions.disableSite({ site }));
  }

  duplicateSite(site: ISite, name: string) {
    this.displaySpinner();
    this.store.dispatch(SignUpActions.duplicateSite({ site, name }));
  }

  addNewSite() {
    this.displaySpinner();
    this.store.dispatch(SignUpActions.addNewSite());
  }

  updateSitePhotoDetails(siteId: number, photos: IPhoto[], continueWizard: boolean = false) {
    this.store.dispatch(SignUpActions.updateSitePhotoDetails({ siteId, photos, continueWizard }));
  }

  updateListingPhotoDetails(listingId: number, photos: IPhoto[], continueWizard: boolean = false) {
    this.store.dispatch(SignUpActions.updateListingPhotoDetails({ listingId, photos, continueWizard }));
  }

  updateBoondockPhotoDetails(boondockId: number, photos: IPhoto[], continueWizard: boolean = false) {
    this.store.dispatch(SignUpActions.updateBoondockPhotoDetails({ boondockId, photos, continueWizard }));
  }

  sendOwnerEmail() {
    this.store.dispatch(SignUpActions.sendOwnerEmail());
  }
}
